import { useState, type ReactNode } from 'react';
import { BrowserRouter } from 'react-router-dom';

/**
 * The path the app is served from, matching `base` in vite.config.ts.
 *
 * Vite exposes it as BASE_URL and always includes a trailing slash; react-router
 * wants none, and an empty basename rather than '/' for a root deployment.
 */
const BASE_PATH = import.meta.env.BASE_URL.replace(/\/$/, '');
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';
import { AppThemeProvider, ConfirmProvider, ToastProvider } from '@radiology/ui';
import { AuthGate } from '../auth/AuthGate';
import { i18n } from '../i18n';

/**
 * Provider stack. Order matters:
 *   i18n -> theme -> toasts -> confirm -> query -> router -> AuthGate
 *
 * The router sits ABOVE AuthGate so the auth screens can navigate, and AuthGate
 * renders either the sign-in/reset screens or the portal beneath it.
 */
export function AppProviders({ children }: { children: ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            retry: 1,
            refetchOnWindowFocus: false,
          },
        },
      }),
  );

  return (
    <I18nextProvider i18n={i18n}>
      <AppThemeProvider>
        <ToastProvider>
          <ConfirmProvider>
            <QueryClientProvider client={queryClient}>
              <BrowserRouter basename={BASE_PATH}>
                <AuthGate>{children}</AuthGate>
              </BrowserRouter>
            </QueryClientProvider>
          </ConfirmProvider>
        </ToastProvider>
      </AppThemeProvider>
    </I18nextProvider>
  );
}
